import React, { useMemo, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { CALC_PRICING } from "../../mock/mockData";

const Calculator = () => {
  const [model, setModel] = useState("Power");
  const [voltage, setVoltage] = useState("48V");
  const [capacity, setCapacity] = useState("20Ah");
  const [qty, setQty] = useState(3);

  const pricing = useMemo(() => {
    return CALC_PRICING[model]?.[voltage]?.[capacity] || { dealer: 0, pvp: 0, margin: 0 };
  }, [model, voltage, capacity]);

  const totals = useMemo(() => {
    const investment = pricing.dealer * qty;
    const revenue = pricing.pvp * qty;
    const totalMargin = pricing.margin * qty;
    return { investment, revenue, totalMargin };
  }, [pricing, qty]);

  const fmt = (n) => n.toLocaleString("pt-PT", { maximumFractionDigits: 0 }) + " €";
  const fmt2 = (n) => n.toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

  return (
    <section id="calculadora" className="border-t-[3px] border-black bg-[#FFD60A]">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-24">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-5">
            <span className="mono text-[11px] uppercase tracking-widest px-3 py-1 brut-border bg-white">06</span>
            <span className="mono text-[11px] uppercase tracking-widest text-black/70">Calculadora</span>
            <div className="flex-1 h-[3px] bg-black" />
          </div>
          <h2 className="display font-bold text-[36px] sm:text-[56px] md:text-[80px] lg:text-[96px] leading-[0.9]">
            Quanto vale
            <br />
            <span className="italic font-normal">o seu próximo</span>
            <br />
            pedido?
          </h2>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-8">
          {/* Form */}
          <div className="brut-border-thick bg-[#F4F0E8] p-6 md:p-8">
            <div className="space-y-6">
              <div>
                <label className="mono text-[11px] uppercase tracking-widest font-bold mb-2 block">Modelo</label>
                <div className="grid grid-cols-3 gap-0 brut-border">
                  {["Power", "Nice", "Briza"].map((m, i) => (
                    <button
                      key={m}
                      onClick={() => setModel(m)}
                      className={`py-3 mono uppercase text-[13px] font-bold tracking-wider transition-colors ${
                        i > 0 ? "border-l-[3px] border-black" : ""
                      } ${model === m ? "bg-black text-[#FFD60A]" : "bg-white hover:bg-[#F4F0E8]"}`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="mono text-[11px] uppercase tracking-widest font-bold mb-2 block">Voltagem</label>
                <div className="grid grid-cols-2 gap-0 brut-border">
                  {["48V", "60V"].map((v, i) => (
                    <button
                      key={v}
                      onClick={() => setVoltage(v)}
                      className={`py-3 mono uppercase text-[13px] font-bold tracking-wider transition-colors ${
                        i > 0 ? "border-l-[3px] border-black" : ""
                      } ${voltage === v ? "bg-[#E63946] text-white" : "bg-white hover:bg-[#F4F0E8]"}`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="mono text-[11px] uppercase tracking-widest font-bold mb-2 block">Capacidade da bateria</label>
                <div className="grid grid-cols-3 gap-0 brut-border">
                  {["20Ah", "24Ah", "30Ah"].map((c, i) => (
                    <button
                      key={c}
                      onClick={() => setCapacity(c)}
                      className={`py-3 mono uppercase text-[13px] font-bold tracking-wider transition-colors ${
                        i > 0 ? "border-l-[3px] border-black" : ""
                      } ${capacity === c ? "bg-black text-[#FFD60A]" : "bg-white hover:bg-[#F4F0E8]"}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="mono text-[11px] uppercase tracking-widest font-bold mb-2 block">
                  Quantidade de unidades (mín. 3)
                </label>
                <div className="flex items-stretch brut-border bg-white">
                  <button
                    onClick={() => setQty(Math.max(3, qty - 1))}
                    className="w-14 grid place-items-center bg-[#F4F0E8] border-r-[3px] border-black hover:bg-black hover:text-[#FFD60A] transition-colors"
                  >
                    <Minus size={18} />
                  </button>
                  <input
                    type="number"
                    value={qty}
                    onChange={(e) => setQty(Math.max(3, parseInt(e.target.value || "3", 10)))}
                    className="flex-1 px-4 py-3 text-center display text-[28px] font-bold bg-white outline-none"
                  />
                  <button
                    onClick={() => setQty(qty + 1)}
                    className="w-14 grid place-items-center bg-[#F4F0E8] border-l-[3px] border-black hover:bg-black hover:text-[#FFD60A] transition-colors"
                  >
                    <Plus size={18} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-0 brut-border">
                <div className="p-3">
                  <div className="mono text-[10px] uppercase tracking-widest text-black/60">P. revendedor/un.</div>
                  <div className="font-bold mono mt-1">{fmt2(pricing.dealer)}</div>
                </div>
                <div className="p-3 border-l-[3px] border-black">
                  <div className="mono text-[10px] uppercase tracking-widest text-black/60">PVP / un.</div>
                  <div className="font-bold mono mt-1">{fmt2(pricing.pvp)}</div>
                </div>
                <div className="p-3 border-l-[3px] border-black bg-[#E63946] text-white">
                  <div className="mono text-[10px] uppercase tracking-widest text-white/80">Margem / un.</div>
                  <div className="font-bold mono mt-1">{fmt2(pricing.margin)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="brut-border-thick bg-black text-white p-6 md:p-10 relative noise overflow-hidden">
            <div className="absolute top-4 right-4 mono text-[10px] uppercase tracking-widest text-[#FFD60A]">Simulação</div>
            <div className="mono text-[12px] uppercase tracking-widest text-[#FFD60A] font-bold mb-2">
              {model} · {voltage} · {capacity} · {qty}×
            </div>
            <div className="space-y-6 mt-8">
              <div className="flex items-end justify-between gap-4 pb-5 border-b-[3px] border-white/20">
                <span className="mono text-[12px] uppercase tracking-widest text-white/70">Investimento total</span>
                <span className="display text-[36px] md:text-[48px] font-bold leading-none">{fmt(totals.investment)}</span>
              </div>
              <div className="flex items-end justify-between gap-4 pb-5 border-b-[3px] border-white/20">
                <span className="mono text-[12px] uppercase tracking-widest text-white/70">Facturação prevista</span>
                <span className="display text-[36px] md:text-[48px] font-bold leading-none">{fmt(totals.revenue)}</span>
              </div>
              <div className="flex items-end justify-between gap-4 pt-2">
                <span className="mono text-[12px] uppercase tracking-widest font-bold text-[#FFD60A]">Margem total</span>
                <span className="display text-[52px] md:text-[72px] font-bold leading-none text-[#FFD60A]">{fmt(totals.totalMargin)}</span>
              </div>
            </div>
            <a
              href="#candidatura"
              className="mt-10 inline-flex w-full sm:w-auto items-center justify-center gap-3 bg-[#E63946] text-white font-bold mono text-[13px] uppercase tracking-wider px-6 py-4 brut-border brut-btn"
            >
              Pedir esta proposta →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Calculator;
